import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, isRef as _isRef, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "my-3" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { key: 1 }

import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { formatDate } from '@/utils/date-utils';
import { useQuery } from '@vue/apollo-composable';
import { UsersDocument, UserType } from '@/gql/graphql';
import { getActiveUserOrganizationAssociation } from '@/utils/user-utils';
import {
  BAlert,
  BButton,
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BPagination,
  BSpinner,
  BTable,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { deepParseDates, formatDateTimeStringToIso } from '@/utils/date-utils';
import { PAGING_ITEMS_PER_PAGE } from '@/constants';
import { openRouterLink } from '@/utils/router-utils';


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminUserListView',
  setup(__props) {

const router = useRouter();

// Filtering and paging state
const id = ref<number | null>(null);
const uuid = ref<string | null>(null);
const givenNamePattern = ref<string | null>(null);
const familyNamePattern = ref<string | null>(null);
const emailPattern = ref<string | null>(null);
const strCreatedAfter = ref<string | null>(null);
const strCreatedBefore = ref<string | null>(null);
const dtCreatedAfter = ref<string | null>(null);
const dtCreatedBefore = ref<string | null>(null);

const pagingPageCurrent = ref(1);
const pagingItemsPerPage = ref(PAGING_ITEMS_PER_PAGE);
const pagingTotalRows = computed(() => {
  return usersQueryResult.value?.users?.total || 0;
});

const users = computed<UserType[]>(() => {
  return (
    usersQueryResult.value?.users?.users
      ?.filter((user) => user && user !== null)
      .map((user) => ({
        ...(deepParseDates(user) as UserType),
      })) || []
  );
});

// GraphQL Query to Get Users
const {
  result: usersQueryResult,
  loading: usersQueryInProgress,
  error: usersQueryError,
  refetch: usersQueryRefetch,
} = useQuery(UsersDocument, {
  id: id.value,
  uuid: uuid.value,
  givenNamePattern: givenNamePattern.value,
  familyNamePattern: familyNamePattern.value,
  emailPattern: emailPattern.value,
  createdAfter: dtCreatedAfter.value,
  createdBefore: dtCreatedBefore.value,
  pagingPageCurrent: pagingPageCurrent.value,
  pagingItemsPerPage: pagingItemsPerPage.value,
});

function clearFilter() {
  id.value = null;
  uuid.value = null;
  givenNamePattern.value = '';
  familyNamePattern.value = '';
  emailPattern.value = '';
  strCreatedAfter.value = null;
  strCreatedBefore.value = null;
  dtCreatedAfter.value = null;
  dtCreatedBefore.value = null;
  pagingPageCurrent.value = 1;

  requestRefetchUsers();
}

// Function to refetch data
function requestRefetchUsers() {
  // check integer values are integer values
  if (!Number.isInteger(id.value)) {
    console.warn('Invalid user ID. Resetting to null.');
    id.value = null;
  }

  // convert date and time to dateTime object (current timezone)
  dtCreatedAfter.value = formatDateTimeStringToIso(strCreatedAfter.value, null);
  dtCreatedBefore.value = formatDateTimeStringToIso(
    strCreatedBefore.value,
    null
  );

  // refetch the query
  usersQueryInProgress.value = true;
  usersQueryError.value = null;

  // refetch the query with the new variables
  usersQueryRefetch({
    id: id.value,
    uuid: uuid.value,
    givenNamePattern: givenNamePattern.value,
    familyNamePattern: familyNamePattern.value,
    emailPattern: emailPattern.value,
    createdAfter: dtCreatedAfter.value,
    createdBefore: dtCreatedBefore.value,
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: pagingItemsPerPage.value,
  })
    ?.catch((err) => {
      console.error('Refetch failed:', err);
      usersQueryError.value = err;
      usersQueryInProgress.value = false;
    })
    .then(() => {
      console.log('refetched');
      usersQueryInProgress.value = false;
    });
}

function onSearchRequested() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1;
  } else {
    requestRefetchUsers();
  }
}

const fields = [
  {
    key: 'createdAt',
    label: 'Created',
    sortable: true,
    formatter: formatDate,
    tdClass: 'align-middle',
  },
  {
    key: 'givenName',
    label: 'Given Name',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'familyName',
    label: 'Family Name',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'email',
    label: 'E-mail',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'organization',
    label: 'Organization',
    sortable: false,
    tdClass: 'align-middle',
  },
  {
    key: 'id',
    label: '',
    tdClass: 'align-middle text-end min-width',
  },
];

watch(pagingPageCurrent, (newPage) => {
  console.debug('page changed to', newPage);
  requestRefetchUsers();
});

function handleUserDetailRequest(
  user: UserType,
  index?: number,
  evt?: MouseEvent
) {
  if (user && user.id) {
    openRouterLink(
      {
        name: 'AdminUserDetail',
        params: { id: user.id },
      },
      router,
      evt
    );
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("h1", null, [
      _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'users'] }),
      _cache[11] || (_cache[11] = _createTextVNode(" Users", -1))
    ]),
    _cache[17] || (_cache[17] = _createElementVNode("h5", null, "Browse registered users", -1)),
    _cache[18] || (_cache[18] = _createElementVNode("hr", null, null, -1)),
    _createElementVNode("div", null, [
      _createVNode(_unref(BAlert), {
        modelValue: _unref(usersQueryError),
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(usersQueryError) ? (usersQueryError).value = $event : null)),
        variant: "danger",
        dismissible: "",
        class: "mt-3"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(usersQueryError)) + " ", 1),
          _createVNode(_unref(BButton), {
            onClick: requestRefetchUsers,
            variant: "outline-dark",
            class: "btn-sm"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'sync'] }),
              _cache[12] || (_cache[12] = _createTextVNode(" Retry", -1))
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(BForm), {
          class: "d-flex flex-row align-items-end flex-wrap gap-2",
          onSubmit: _withModifiers(onSearchRequested, ["prevent"])
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BFormGroup), {
              label: "User ID",
              "label-for": "user-id"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "user-id",
                  type: "number",
                  modelValue: id.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((id).value = $event)),
                  modelModifiers: { number: true },
                  placeholder: "User ID"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "User UUID",
              "label-for": "uuid"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "uuid",
                  type: "text",
                  modelValue: uuid.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((uuid).value = $event)),
                  placeholder: "e.g. 98a21803-733c-4323-886e-257ba4c27f5e"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Given name",
              "label-for": "given-name"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "given-name",
                  type: "text",
                  modelValue: givenNamePattern.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((givenNamePattern).value = $event)),
                  placeholder: "e.g. John"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Family name",
              "label-for": "family-name"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "family-name",
                  type: "text",
                  modelValue: familyNamePattern.value,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((familyNamePattern).value = $event)),
                  placeholder: "e.g. Doe"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "E-mail",
              "label-for": "email"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "email",
                  type: "text",
                  modelValue: emailPattern.value,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((emailPattern).value = $event)),
                  placeholder: "e.g. joe@email.com"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Registered after",
              "label-for": "date-registered-after"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "date-registered-after",
                  type: "date",
                  modelValue: strCreatedAfter.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((strCreatedAfter).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Registered before",
              "label-for": "date-registered-before"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "date-registered-before",
                  type: "date",
                  modelValue: strCreatedBefore.value,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((strCreatedBefore).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BButton), {
              variant: "outline-secondary",
              onClick: clearFilter
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(FontAwesomeIcon), {
                  icon: "trash",
                  class: "me-1"
                }),
                _cache[13] || (_cache[13] = _createTextVNode(" Clear filter ", -1))
              ]),
              _: 1
            }),
            _createVNode(_unref(BButton), {
              variant: "outline-dark",
              type: "submit",
              disabled: _unref(usersQueryInProgress)
            }, {
              default: _withCtx(() => [
                (_unref(usersQueryInProgress))
                  ? (_openBlock(), _createElementBlock("span", _hoisted_2, [
                      _createVNode(_unref(BSpinner), {
                        small: "",
                        label: ""
                      }),
                      _cache[14] || (_cache[14] = _createTextVNode(" Searching ", -1))
                    ]))
                  : (_openBlock(), _createElementBlock("span", _hoisted_3, [
                      _createVNode(_unref(FontAwesomeIcon), {
                        icon: "magnifying-glass",
                        class: "me-1"
                      }),
                      _cache[15] || (_cache[15] = _createTextVNode(" Refresh ", -1))
                    ]))
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          _: 1
        })
      ]),
      (pagingTotalRows.value > pagingItemsPerPage.value)
        ? (_openBlock(), _createBlock(_unref(BPagination), {
            key: 0,
            modelValue: pagingPageCurrent.value,
            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((pagingPageCurrent).value = $event)),
            "total-rows": pagingTotalRows.value,
            "per-page": pagingItemsPerPage.value,
            pills: "",
            size: "sm"
          }, null, 8, ["modelValue", "total-rows", "per-page"]))
        : _createCommentVNode("", true),
      (!users.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[16] || (_cache[16] = [
            _createElementVNode("h5", { class: "my-5 py-5 text-center" }, "There are no users.", -1)
          ]))]))
        : (_openBlock(), _createBlock(_unref(BCard), {
            key: 2,
            class: "mb-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BTable), {
                striped: "",
                responsive: "",
                hover: "",
                items: users.value,
                fields: fields,
                busy: _unref(usersQueryInProgress),
                class: "browser-table",
                onRowClicked: handleUserDetailRequest
              }, {
                "cell(name)": _withCtx((data) => [
                  _createElementVNode("strong", null, _toDisplayString(data.value), 1)
                ]),
                "cell(organization)": _withCtx((props) => [
                  _createElementVNode("span", null, _toDisplayString(_unref(getActiveUserOrganizationAssociation)(props.item)?.organization
                  ?.name || '-'), 1)
                ]),
                "cell(id)": _withCtx((data) => [
                  _createVNode(_unref(BButton), {
                    variant: "outline-secondary",
                    class: "btn-sm",
                    onClick: [
                      ($event: any) => (handleUserDetailRequest(data.item, data.item.id, $event)),
                      _cache[9] || (_cache[9] = _withModifiers(() => {}, ["stop"]))
                    ]
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(FontAwesomeIcon), { icon: ['fa', 'magnifying-glass'] })
                    ]),
                    _: 1
                  }, 8, ["onClick"])
                ]),
                _: 1
              }, 8, ["items", "busy"])
            ]),
            _: 1
          })),
      (pagingTotalRows.value > pagingItemsPerPage.value)
        ? (_openBlock(), _createBlock(_unref(BPagination), {
            key: 3,
            modelValue: pagingPageCurrent.value,
            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((pagingPageCurrent).value = $event)),
            "total-rows": pagingTotalRows.value,
            "per-page": pagingItemsPerPage.value,
            pills: "",
            size: "sm"
          }, null, 8, ["modelValue", "total-rows", "per-page"]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})